import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import { Box } from '../../../../../legacy-design-system/form'
import { JiraField } from './jira-field'
import { css } from '@emotion/css'
import {
  BugtrackerConfig,
  FormProjectField,
  ProjectField
} from '../../api/shared'
import { Label } from '../../../../../legacy-design-system/form-label'
import { useIntl } from 'react-intl'
import { useProjectFieldsQuery } from '../../api/use-project-fields-query'
import { useJiraIntegrationPage } from '../jira-integration-page.provider'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { JiraFormFieldTypes } from '../jira-form-field-types'
import { transformFormDataToBugtrackerRequest } from '../../../bugtrackers/api/build-api-payload-bugtracker-credentials'
import { JiraFieldDefaultValue } from './jira-field-default-value'
import { JiraFieldRow } from './jira-field-row'
import { DeleteJiraFieldButton } from './delete-jira-field-button'
import { AddJiraFieldLink } from './add-jira-field-link'
import { JiraIntegrationForm } from '../jira-form-schema'
import { uuidv4 } from '../../../../../util'

const convertApiProjectFieldToFormProjectField = (
  field: ProjectField,
  value?: any
): FormProjectField => {
  return {
    _projectField: field,
    key: field?.key,
    type: field?.schema?.type,
    value: value ?? '',
    items: field?.schema?.items,
    id: field?.key
  }
}

const filterRequiredProjectFields = (
  projectFields?: ProjectField[]
): ProjectField[] => {
  return projectFields
    ? projectFields.filter((projectField) => projectField.required)
    : []
}

const filterOptionalProjectFields = (
  projectFields?: ProjectField[]
): ProjectField[] => {
  return projectFields
    ? projectFields.filter((projectField) => !projectField.required)
    : []
}

interface UseLoadProjectFieldsProps {
  organizationUuid: string
  projectKey?: string
  issueTypeId?: number
  formFields: FormProjectField[]
  replaceFormFields: (fields: any) => void
}

const initializeRequiredFormFields = (
  keepFormValues: boolean,
  projectFieldsQueryData: ProjectField[],
  replaceFormFields: (value: FormProjectField[]) => void,
  getValues: (values: string) => any
) => {
  const currentFormFields: FormProjectField[] = keepFormValues
    ? getValues(JiraFormFieldTypes.PROJECT_FIELDS)
    : []

  const newFormFields: FormProjectField[] = filterRequiredProjectFields(
    projectFieldsQueryData
  ).map((requiredField) => {
    let newValue
    if (keepFormValues) {
      const matchingField = currentFormFields.find(
        (currentFormField) => currentFormField.key === requiredField.key
      )
      newValue = matchingField?.value
    }
    return convertApiProjectFieldToFormProjectField(requiredField, newValue)
  })
  replaceFormFields(newFormFields)
}

export const useLoadProjectFields = ({
  organizationUuid,
  projectKey,
  issueTypeId,
  formFields,
  replaceFormFields
}: UseLoadProjectFieldsProps): {
  requiredFields: ProjectField[]
  optionalFields: ProjectField[]
} => {
  const { getValues } = useFormContext<JiraIntegrationForm>()
  const isInitialProjectFieldDataLoadRef = useRef(true)

  const projectFieldsQueryResponse = useProjectFieldsQuery({
    organizationUuid,
    bugtrackerRequest: transformFormDataToBugtrackerRequest(getValues),
    projectId: projectKey,
    issueTypeId
  })

  const projectFieldsQueryData = projectFieldsQueryResponse?.data?.fields

  const requiredFields: ProjectField[] = useMemo(
    () => filterRequiredProjectFields(projectFieldsQueryData),
    [projectFieldsQueryData]
  )

  const optionalFields: ProjectField[] = useMemo(
    () => filterOptionalProjectFields(projectFieldsQueryData),
    [projectFieldsQueryData]
  )

  useEffect(() => {
    if (projectFieldsQueryData && projectFieldsQueryData.length > 0) {
      const initialize = (keepFormValues: boolean) => {
        initializeRequiredFormFields(
          keepFormValues,
          projectFieldsQueryData,
          replaceFormFields,
          getValues
        )
      }

      if (isInitialProjectFieldDataLoadRef.current) {
        isInitialProjectFieldDataLoadRef.current = false
        if (formFields && formFields.length > 0) {
          //  Condition #1: Initial data load - Edit Configuration Mode (due to form fields EXISTING)
          //  Add additional API project metadata to each form field, for UI form elements to reference
          const newFormFields = formFields.map((formField) => {
            return {
              ...formField,
              _projectField: projectFieldsQueryData.find(
                (projectField) => projectField.key === formField.key
              )
            }
          })
          replaceFormFields(newFormFields)
        } else {
          //  initialize #2: Initial data load - New Configuration Mode (due to form fields MISSING)
          initialize(true)
        }
      } else {
        //  Condition #3: After initial data load - New or Edit Configuration mode
        initialize(false)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectFieldsQueryData])

  return { requiredFields, optionalFields }
}

export const AdditionalJiraFieldsList: FC<{
  bugtracker?: BugtrackerConfig
}> = ({ bugtracker }) => {
  const { formatMessage } = useIntl()
  const {
    state: { organizationUuid }
  } = useJiraIntegrationPage()
  const { watch } = useFormContext<JiraIntegrationForm>()

  const {
    fields: formFields,
    append,
    remove,
    update,
    replace
  } = useFieldArray<JiraIntegrationForm, 'fields'>({
    name: JiraFormFieldTypes.PROJECT_FIELDS
  })

  const selectedIssueType = watch(JiraFormFieldTypes.ISSUE_TYPE)
  const projectValue = watch(JiraFormFieldTypes.PROJECT)

  const projectKey =
    typeof projectValue === 'object' ? projectValue?.key : undefined
  const issueTypeId =
    typeof selectedIssueType === 'object' ? selectedIssueType?.id : undefined

  const [remainingFields, setRemainingFields] = useState<ProjectField[]>([])

  const { requiredFields, optionalFields } = useLoadProjectFields({
    organizationUuid,
    projectKey,
    issueTypeId,
    formFields,
    replaceFormFields: replace
  })

  useEffect(() => {
    setRemainingFields(
      optionalFields.filter(
        (projectField) =>
          formFields.findIndex(
            (formField) =>
              formField._projectField &&
              formField._projectField.key === projectField.key
          ) < 0
      )
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formFields])

  const addField = () => {
    append({
      id: uuidv4()
    })
  }

  const updateFieldSelection = (index: number, field: ProjectField) => {
    const newValue = convertApiProjectFieldToFormProjectField(field)
    update(index, newValue)
  }

  const getUpdateFieldValue = (
    index: number,
    field: FormProjectField
  ): ((value: any) => void) => {
    return (value: any) => {
      update(index, {
        ...field,
        value
      })
    }
  }

  const removeField = (index: number) => {
    remove(index)
  }

  if (!optionalFields.length && !requiredFields.length) {
    return null
  }
  return (
    <Box
      basis={7}
      className={css`
        display: flex;
        flex-direction: column;
        gap: 10px;
        padding-right: 15px;
      `}
    >
      <JiraFieldRow>
        <Label>
          {formatMessage({
            id: 'title_integrationsSettings_additionalJiraFields'
          })}
        </Label>
        <Label>
          {formatMessage({
            id: 'title_integrationsSettings_defaultValue'
          })}
        </Label>
      </JiraFieldRow>
      {formFields.map((formField, index) => {
        return (
          <JiraFieldRow key={`jira-field-${formField.key || formField.id}`}>
            <JiraField
              field={formField}
              fieldFormIndex={index}
              updateFieldSelection={updateFieldSelection}
              selectableFields={remainingFields}
            />
            <JiraFieldDefaultValue
              field={formField}
              updateValue={getUpdateFieldValue(index, formField)}
              bugtracker={bugtracker}
            />
            <DeleteJiraFieldButton
              field={formField}
              fieldIndex={index}
              removeField={removeField}
            />
          </JiraFieldRow>
        )
      })}
      {formFields.length < optionalFields.length + requiredFields.length && (
        <AddJiraFieldLink addField={addField} />
      )}
    </Box>
  )
}
